import React, { useEffect, useState, Suspense, lazy } from 'react';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { detect } from 'detect-browser';
import { BrowserRouter as Router, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { retrieveAccount } from './account';
import NoIeSupport from './components/Helpers/Errors/Templates/NoIeSupport';
import ProtectedRoute from './components/Helpers/ProtectedRoute';
import Layout from './components/Layout/Layout';
import { setDefaultLinks } from './components/Layout/LayoutConst';
import Theme from './components/Theme/Theme';
import { saveSessionEvent } from './session';
import { ADVISOR_OBJECT_KEY, getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY, SESSION_STORAGE_IS_SIGN_WITHOUT_AUTH, SESSION_STORAGE_ROUTING_KEY, setSessionStorageItem, setSessionStorageItemWithExpiration } from './sessionStorage';
import SessionTimeout from './SessionTimeout';
import constants from './constants';
import { IndexUniversalLifePath, TermLifeInsurancePath, SubmitACasePath, RequestAnIllustrationPath, AssetBasedLTCPath, GuaranteedUniversalLifePath, ActivityCenterDetailsPath, ActivityCenterPath, NeedsAnalysisPath, TermConversionRequestPath, UniversalLifePath, SPLPath, TermConversionPath, ResourceCenterPath, PolicyReviewPath, TermReviewPath, PermReviewPath, SignNowPath, ActionStatusPath, UnauthorizedPath, StateDisclosurePath, ProductLandingPath, ProductPath, ActionConsentPath, ActionSignPath, PermanentLifePath } from './components/Common/Products/ProductsRoute';
import AuthLandingPage from './components/Home/AuthLandingPage';
import { getAuthToken, setAuthToken, refreshToken, validateToken, getNameID } from './auth';
import { httpRequest } from './httpRequest';
import { checkPermission } from './components/Helpers/CheckAccess';

const browser = detect();
const isIe = browser.name === 'ie';
const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

// Lazy-loaded components
const AdvisorWelcome = lazy(() => import('./components/Home/AdvisorWelcome'));
const ActivityCenter = lazy(() => import('./components/ActivityCenter-V2/ActivityCenter'));
const CaseDetails = lazy(() => import('./components/ActivityCenter-V2/CaseDetails'));
const ProductLandingPage = lazy(() => import('./components/ProductsLandingContents/ProductLandingPage'));
const ProductsPage = lazy(() => import('./components/ProductsLandingContents/Products'));
const PermanentLife = lazy(() => import('./components/PermanentLife/PermanentLife'));
const NeedsAnalysis = lazy(() => import('./components/InsuranceTools/NeedsAnalysis/NeedsAnalysis'));
const PolicyReview = lazy(() => import('./components/PolicyReview/PolicyReview'));
const TermReview = lazy(() => import('./components/PolicyReview/TermReview/TermReview'));
const PermReview = lazy(() => import('./components/PolicyReview/PermReview/PermReview'));
const TermConversion = lazy(() => import('./components/InsuranceTools/TermConversionRequest/TermConversion'));
const TermConversionRequest = lazy(() => import('./components/TermConversionRequest/TermConversionRequest'));
const ResourceCenter = lazy(() => import('./components/ResourceCenter/ResourceCenter'));
const RequestIllustration = lazy(() => import('./components/Product/RequestIllustration/RequestIllustration'));
const SubmitCase = lazy(() => import('./components/Product/SubmitCase/SubmitCase'));
const TermLifeInsurance = lazy(() => import('./components/Product/TermLifeInsurance/TermLifeInsurance'));
const GuaranteedUniversalLife = lazy(() => import('./components/Product/GUL/GuaranteedUniversalLife'));
const SPL = lazy(() => import('./components/Product/SPL/SPL'));
const IndexUniversalLife = lazy(() => import('./components/Product/Estimators/IndexUniversalLife/IndexUniversalLife'));
const UniversalLife = lazy(() => import('./components/Product/Estimators/UniversalLife/UniversalLife'));
const AssetBasedLTC = lazy(() => import('./components/Product/Estimators/AssetBasedLTC/AssetBasedLTC'));
const NewSession = lazy(() => import('./components/NewSession'));
const EsignReturn = lazy(() => import('./components/Product/Common/Pages/EsignReturn'));
const StateDisclosure = lazy(() => import('./components/Disclosure/StateDisclosure'));
const Status = lazy(() => import('./components/Product/Common/Pages/Status/Status'));
const SignerAuth = lazy(() => import('./components/Product/Common/Pages/SignerAuth'));
const Unauthorized = lazy(() => import('./components/Quoters/Common/Unauthorized'));
const Sign = lazy(() => import('./components/Product/SPL/steps/Sign'));
const ConsentToElectronicSignatureWithoutAuth = lazy(() => import('./components/Product/SPL/steps/ConsentToElectronicSignatureWithoutAuth'));

const PageNotFound = () => {
  return (
    <div className="container">
      <div className="jumbotron mt-4">Page not found.</div>
    </div>
  );
};

let primaryColor = null;
let secondaryColor = null;

const setColorsFromAccount = (acc) => {
  primaryColor = acc ? acc.primaryColor : null;
  secondaryColor = acc ? acc.primaryColor : null;
};

const App = () => {
  const history = useHistory();
  const isAuthLanding = /^\/sso\/Authenticate(\/.*)?$/.test(window.location.pathname);
  const isLogOut = new URLSearchParams(window.location.search).get('logout') === 'true';

  const location = useLocation();
  const [loadingSite, setLoadingSite] = useState(true);
  const [error, setError] = useState(false);
  const [user, setUser] = useState();
  const [validAuth, setValidAuth] = useState(false);
  const [invalidSignOn, setInvalidSignOn] = useState(false);
  const [account, setAccount] = useState();
  const [navLinks, setActiveNavLink] = useState();
  const [roleError, setRoleError] = useState(false);
  const [isFirmUser, setFirmUser] = useState(false); // A firm user is someone who accesses the platform using the authentication URL.
  const isSignWithoutAuth = (["/Action/Status", "/Action/SignNow"].includes(window.location.pathname) && !user) || JSON.parse(sessionStorage.getItem(SESSION_STORAGE_IS_SIGN_WITHOUT_AUTH));  // isSignWithoutAuth will be true only if the user clicks the link from the email.
  const bypassAuthentication = account && account.authenticationRequiredByPass;

  useEffect(() => {
    if (isAuthLanding) {
      setFirmUser(true); // Set isFirmUser true when the authentication URL is accessed.
    }
  }, [isAuthLanding]);

  const setActiveLink = (currentIndex) => {
    const navItems = navLinks;
    navItems.forEach((part, index, array) => {
      array[index].isActive = false;
    });
    navItems[currentIndex].isActive = true;

    setActiveNavLink([...navItems]);
  };

  const getNameIdForAuthenticationNotRequired = () => {
    return new URLSearchParams(window.location.search).get('testNameID');
  }

  const handleAuthentication = async () => {
    if (validAuth) return;
    try {
      if (!getAuthToken()) {
        setValidAuth(false);
        setLoadingSite(false);
      } else {
        let isValid = await validateToken();
        if (!isValid) {
          setLoadingSite(false);
        }
        setValidAuth(isValid);
      }
    } catch (error) {
      console.error('Error during authentication:', error);
    }
  };

  useEffect(() => {
    if (isAuthLanding) {
      if (getAuthToken() && validAuth) {
        setLoadingSite(true);
        history.push('/');
      }
    }
  }, [validAuth]);

  useEffect(() => {
    if (bypassAuthentication !== undefined) {
      handleAuthentication();
    }
  }, [account]);

  useEffect(() => {
    saveSessionEvent();
    // Added devtest mode for case status page
    const isDevTestMode = location.search && location.search.toLowerCase().includes('devtest');
    if (isDevTestMode) {
      sessionStorage.setItem(SESSION_STORAGE_ROUTING_KEY, JSON.stringify({ devTestMode: true }));
    }
  }, [location]);

  useEffect(() => {
    const navItems = setDefaultLinks(account);
    setActiveNavLink([...navItems]);
  }, [location, account]);

  useEffect(() => {

    const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);

    if (!accountObj) {
      retrieveAccount()
        .then((account) => {
          setSessionStorageItemWithExpiration(SESSION_STORAGE_ACCOUNT_KEY, account, 60);
          setColorsFromAccount(account.account);
          setAccount(account);
        })
        .catch(() => {
          setError(true);
          setLoadingSite(false);
          return;
        });
    } else {
      setColorsFromAccount(accountObj.account);
      setAccount(accountObj);
    }
  }, []);

  useEffect(() => {
    if (!account || (!bypassAuthentication && !validAuth)) {
      return;
    }
    const nameID = (!bypassAuthentication || getAuthToken()) ? getNameID() : getNameIdForAuthenticationNotRequired();
    const advisorObj = getSessionStorageItem(ADVISOR_OBJECT_KEY);

    // Handle case where authentication is bypassed but required user details are missing like "testNameID" is unavailable, the user is not a firm user, and advisor details are also unavailable.
    if (bypassAuthentication && !nameID && !isFirmUser && !advisorObj) {
      setInvalidSignOn(true);
      setLoadingSite(false);
      return;
    }

    const getAdvisor = () => {
      httpRequest.get(`${covrServicesUrl}/api/Client/SSO/Authenticate?oneClickAccountId=${account.account.oneClickAccountId}&testNameID=${nameID}`)
        .then((data) => {
          const role = data.role;
          const rolesWithPlatformAccess = [
            constants.faRole,
            constants.opsRole,
            constants.adminRole,
          ];

          if (role && rolesWithPlatformAccess.includes(role)) {
            const userInfo = {
              agentID: data.agentID,
              userID: data.userID,
              nameID: data.nameID,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phone: data.phone,
              iscName: data.iscName,
              firstTimeLogin: data.firstTimeLogin,
              premiumGoal: data.premiumGoal,
              appGoal: data.appGoal,
              role: data.role,
              ricEmail: data.ricEmail,
              ricWorkPhone: data.ricWorkPhone,
              ricBiography: data.ricBiography,
              ricCalendlyURL: data.ricCalendlyURL,
              ricProfileImageURL: data.ricProfileImageURL,
              aipProfileImageURL: data.aipProfileImageURL,
              sessionTimeout: data.sessionTimeout,
              caseManagerBio: data.caseManagerBio,
              caseManagerCalendlyURL: data.caseManagerCalendlyURL,
              caseManagerEmail: data.caseManagerEmail,
              caseManagerFullName: data.caseManagerFullName,
              caseManagerProfileImageURL: data.caseManagerProfileImageURL,
              caseManagerWorkPhone: data.caseManagerWorkPhone,
              citiEnableCopyTicket: data.citiEnableCopyTicket,
              enableSearchByFinancialAdvisor: data.enableSearchByFinancialAdvisor,
              enableSearchByInsured: data.enableSearchByInsured,
            };
            setSessionStorageItem(ADVISOR_OBJECT_KEY, userInfo);

            if (bypassAuthentication && data.accessToken && !getAuthToken()) {
              setAuthToken(data.accessToken);
            }

            const navItems = setDefaultLinks(account);
            setActiveNavLink([...navItems]);

            setUser(userInfo);
            setLoadingSite(false);
            setInvalidSignOn(false);
          } else {
            setLoadingSite(false);
            setRoleError(true);
          }
        }, () => {
          setInvalidSignOn(true);
          setLoadingSite(false);
        })
        .catch(() => {
          setInvalidSignOn(true);
          setLoadingSite(false);
        });
    };

    if (!advisorObj && nameID) {
      getAdvisor();
    } else {
      setUser(advisorObj);
      setLoadingSite(false);
    }
  }, [account, validAuth]);

  if (isIe) {
    return (
      <div>
        <NoIeSupport />
      </div>
    );
  }

  function PrivateRoute(path, component, permission = '', exact = false, permissionKey = null) {
    if (permissionKey && !isSignWithoutAuth) {
      return (
        <ProtectedRoute
          exact={exact}
          path={path}
          component={component}
          appGoal={user && user.appGoal}
          premiumGoal={user && user.premiumGoal}
          user={user}
          permissionKey={permissionKey}
          setActiveLink={setActiveLink}
          navLinks={navLinks}
        />
      );
    }
    const isPermissionGranted = (permission.length > 0 && checkPermission(permission)) || permission === '';
    const isUnauthenticatedAccess = isSignWithoutAuth && !["/Action/Status", "/Action/SignNow", "/Action/Consent", "/Action/Sign"].includes(path);
    return (
      <Route
        exact={exact}
        path={path}
        component={(!isPermissionGranted || isUnauthenticatedAccess) ? Unauthorized : component}
      />
    );
  }

  // Use for handle return path by docusign
  if (location.pathname.includes('esign-return')) {
    return <Suspense fallback={<div className="d-flex justify-content-center p-5">
      <CircularProgress /></div>}><EsignReturn /></Suspense>
  }

  return (
    <React.Fragment>
      {(isAuthLanding) && (
        <div className="d-flex justify-content-center mt-4">
          <AuthLandingPage setValidAuth={setValidAuth} />
        </div>
      )}

      {/* Ensures there are no errors, the site is not loading, and the user is not on the auth landing page,
          Checks that the user is available and either has valid authentication or authentication bypass enabled,
          Ensures sign-on is not invalid, no role-based errors exist, and the user is not logged out.
          Also allows rendering if "sign-in without authentication" is enabled and an account exists. */}

      {((!error && !loadingSite && !isAuthLanding && user && (validAuth || bypassAuthentication) && !invalidSignOn && !roleError && !isLogOut) || (isSignWithoutAuth && account)) && (
        <ThemeProvider theme={Theme.get(primaryColor, secondaryColor)}>
          <Router>
            <Layout
              navLinks={navLinks}
              iscName={user && user.iscName}
              setActiveNavLink={setActiveLink}
              isSignWithoutAuth={isSignWithoutAuth}
            >
              <Suspense fallback={<div className="d-flex justify-content-center p-5">
                <CircularProgress /></div>}>
                <Switch>
                  {PrivateRoute("/", AdvisorWelcome, '', true, "Home")}

                  {PrivateRoute(`${ActivityCenterDetailsPath}:typeId/:caseId`, CaseDetails, '', false, "case-view")}
                  {PrivateRoute(ActivityCenterPath, ActivityCenter, '', true, "ActivityCenter")}

                  {PrivateRoute(RequestAnIllustrationPath, RequestIllustration, 'enableRequestIllustration')}
                  {PrivateRoute(`${RequestAnIllustrationPath}:path`, RequestIllustration, 'enableRequestIllustration')}

                  {PrivateRoute(SubmitACasePath, SubmitCase, 'enableSubmitCase')}
                  {PrivateRoute(`${SubmitACasePath}:path`, SubmitCase, 'enableSubmitCase')}

                  {PrivateRoute(SPLPath, SPL, 'enableSPWLQuoterQuickLink')}
                  {PrivateRoute(`${SPLPath}:path`, SPL, 'enableSPWLQuoterQuickLink')}

                  {PrivateRoute(NeedsAnalysisPath, NeedsAnalysis, 'enableNeedsAnalysis')}

                  {PrivateRoute(`${ProductLandingPath}:product`, ProductLandingPage, '', false, "Landing")}
                  {PrivateRoute(ProductPath, ProductsPage, '', false, "Products")}

                  {PrivateRoute(PermanentLifePath, PermanentLife, 'enablePermPage')}

                  {PrivateRoute(TermLifeInsurancePath, TermLifeInsurance, 'enableTermQuoterQuickLink')}
                  {PrivateRoute(`${TermLifeInsurancePath}:path`, TermLifeInsurance, 'enableTermQuoterQuickLink')}

                  {PrivateRoute(IndexUniversalLifePath, IndexUniversalLife, 'enableIULEstimatorQuickLink')}
                  {PrivateRoute(`${IndexUniversalLifePath}:path`, IndexUniversalLife, 'enableIULEstimatorQuickLink')}

                  {PrivateRoute(UniversalLifePath, UniversalLife, 'enableULEstimatorQuickLink')}
                  {PrivateRoute(`${UniversalLifePath}:path`, UniversalLife, 'enableULEstimatorQuickLink')}

                  {PrivateRoute(AssetBasedLTCPath, AssetBasedLTC, 'enableLTCEstimatorQuickLink')}
                  {PrivateRoute(`${AssetBasedLTCPath}:path`, AssetBasedLTC, 'enableLTCEstimatorQuickLink')}

                  {PrivateRoute(GuaranteedUniversalLifePath, GuaranteedUniversalLife, 'enableGULQuoterQuickLink')}
                  {PrivateRoute(`${GuaranteedUniversalLifePath}:path`, GuaranteedUniversalLife, 'enableGULQuoterQuickLink')}

                  {PrivateRoute(ResourceCenterPath, ResourceCenter, 'enableResourceCenter')}

                  {PrivateRoute(PolicyReviewPath, PolicyReview, 'enablePolicyReview')}

                  {PrivateRoute(TermConversionRequestPath, TermConversionRequest, 'enableTermConversion')}
                  {PrivateRoute(TermConversionPath, TermConversion, 'enableTermConversion')}
                  {PrivateRoute(`${TermConversionPath}:path`, TermConversion, 'enableTermConversion')}

                  {PrivateRoute(TermReviewPath, TermReview, 'enablePolicyReview')}
                  {PrivateRoute(`${TermReviewPath}:path`, TermReview, 'enablePolicyReview')}

                  {PrivateRoute(PermReviewPath, PermReview, 'enablePolicyReview')}
                  {PrivateRoute(`${PermReviewPath}:path`, PermReview, 'enablePolicyReview')}

                  {PrivateRoute(StateDisclosurePath, StateDisclosure)}

                  {PrivateRoute(ActionStatusPath, Status)}
                  {PrivateRoute(SignNowPath, () => <SignerAuth isSignWithoutAuth={isSignWithoutAuth} />)}
                  {isSignWithoutAuth && PrivateRoute(ActionConsentPath, ConsentToElectronicSignatureWithoutAuth)}
                  {isSignWithoutAuth && PrivateRoute(ActionSignPath, Sign)}
                  {PrivateRoute(UnauthorizedPath, Unauthorized)}
                  {PrivateRoute("/ns", () => <NewSession setUser={setUser} />)}
                  {PrivateRoute("*", PageNotFound)}
                </Switch>
              </Suspense>
            </Layout>
          </Router>
        </ThemeProvider>
      )}

      {(loadingSite || (isSignWithoutAuth && !account)) && (
        <div className="d-flex justify-content-center mt-4">
          <CircularProgress />
        </div>
      )}

      {error && (
        <div className="d-flex justify-content-center mt-4">
          <p>The account you are trying to access does not exist. Please check the URL and try again.</p>
        </div>
      )}
      {!loadingSite && !user && (invalidSignOn || ((!validAuth && !bypassAuthentication) && !isAuthLanding)) && !isSignWithoutAuth && (
        <div className="d-flex justify-content-center mt-4">
          <p>
            Access to the Covr platform was denied. Your login information was invalid or your current Covr session was timed out due to inactivity.
          </p>
        </div>
      )}

      {isLogOut && (
        <div className="d-flex justify-content-center mt-4">
          <p>
            <b>You have signed out successfully.</b>
          </p>
        </div>
      )}

      {roleError && (
        <div className="d-flex justify-content-center mt-4">
          <p>At this time your role has restricted access to this platform. Future enhancements coming soon.</p>
        </div>
      )}

      {!loadingSite && user && !isSignWithoutAuth && !invalidSignOn && !roleError && !isLogOut && !isAuthLanding && (
        <SessionTimeout />
      )}
    </React.Fragment>
  );
};

export default App;



// WEBPACK FOOTER //
// ./src/App.js